import { useMemo, useRef } from 'react';
import { compareDuelsForListing } from '@/helpers/duels-sort';
import {
  initialFrozenDuelOrder,
  nextFrozenDuelOrder,
  orderDuelsByFrozenIds,
} from '@/helpers/duels-stable-order';
import type { UiDuel } from '@/hooks/data/duels';

/**
 * Freeze duel card order for a listing scope (GW / filters).
 * Vote count changes must not reshuffle; new ids (infinite scroll) append at the end.
 * Order is derived synchronously so newcomers appear in the same render as the data update.
 */
export function useStableDuelOrder(duels: UiDuel[], scopeKey: string): UiDuel[] {
  const frozenScopeRef = useRef<string | null>(null);
  const frozenOrderRef = useRef<string[]>([]);

  return useMemo(() => {
    if (!scopeKey) {
      frozenScopeRef.current = null;
      frozenOrderRef.current = [];
      return [...duels].sort(compareDuelsForListing);
    }

    if (duels.length === 0) {
      return [];
    }

    if (frozenScopeRef.current !== scopeKey) {
      frozenScopeRef.current = scopeKey;
      frozenOrderRef.current = initialFrozenDuelOrder(duels);
    } else {
      frozenOrderRef.current = nextFrozenDuelOrder(frozenOrderRef.current, duels);
    }

    return orderDuelsByFrozenIds(duels, frozenOrderRef.current);
  }, [duels, scopeKey]);
}
